import { CheckSquare } from 'lucide-react'
import { TaskAlertBadge } from '@/components/tasks/board/TaskBadges'
import { useTaskDrawer } from '@/contexts/TaskDrawerContext'
import { weekBands } from '@/lib/hoje'
import { bandLabel, formatDay, type TaskView } from '@/lib/tasks-board'
import { taskBoardService } from '@/services/task-board-service'
import { EmptyLine, HojeSection } from './HojeSection'
import { useHojeLoader } from './useHojeLoader'

const PER_BAND = 5

function TaskRow({ task, onOpen }: { task: TaskView; onOpen: (task: TaskView) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(task)}
        className="grid w-full grid-cols-[1fr_auto] items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted/50"
      >
        <span className="min-w-0">
          <span className="block truncate font-medium">{task.title}</span>
          {task.case_number && <span className="block truncate text-xs text-muted-foreground">{task.case_number}</span>}
        </span>
        <span className="flex items-center gap-1.5">
          <TaskAlertBadge task={task} />
          <span className="text-xs tabular-nums text-muted-foreground">{formatDay(task.data_referencia)}</span>
        </span>
      </button>
    </li>
  )
}

/** Prazos internos da semana (minhas tarefas abertas), por faixa. Clique abre a gaveta. */
export function HojePrazos() {
  const drawer = useTaskDrawer()
  const state = useHojeLoader(() => taskBoardService.painel({ vista: 'minha-semana' }), drawer.version)
  const open = (task: TaskView) => drawer.openTask(task.id)

  return (
    <HojeSection title="Prazos da semana" description="Seus prazos internos até 7 dias" icon={CheckSquare} href="/tasks" state={state}>
      {(page) => {
        const bands = weekBands(page.tarefas)
        if (bands.length === 0) return <EmptyLine>Nenhum prazo interno nesta semana.</EmptyLine>
        return (
          <div className="flex flex-col gap-3">
            {bands.map((band) => (
              <section key={band.key} aria-label={bandLabel(band.key)}>
                <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
                  {bandLabel(band.key)}
                  <span className="tabular-nums text-muted-foreground">({band.tasks.length})</span>
                </h3>
                <ul className="flex flex-col">
                  {band.tasks.slice(0, PER_BAND).map((task) => <TaskRow key={task.id} task={task} onOpen={open} />)}
                </ul>
                {band.tasks.length > PER_BAND && (
                  <p className="px-2 text-xs text-muted-foreground">e mais {band.tasks.length - PER_BAND}</p>
                )}
              </section>
            ))}
          </div>
        )
      }}
    </HojeSection>
  )
}
